// ─────────────────────────────────────────────────────────────────
// Glass-i — Bank Reconciliation + Cheque Tracking API client (Stage 6E.1).
//
// Thin axios wrappers around /api/erp/accounts/bank-reconciliations/*
// and /api/erp/accounts/cheques/*. The Approval-MFA challenge id flows
// through the standard `x-approval-mfa-challenge-id` header for the
// destructive / state-transition routes (delete / lock / cancel) and
// for cheque bounce / cancel / stop status updates.
// ─────────────────────────────────────────────────────────────────

import client from '../../../../api/client';

const BASE = '/api/erp/accounts';
const BR   = `${BASE}/bank-reconciliations`;
const CHQ  = `${BASE}/cheques`;

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

function mfaHeader(challengeId) {
  return challengeId
    ? { headers: { 'x-approval-mfa-challenge-id': challengeId } }
    : undefined;
}

export const bankReconciliationApi = {
  // ── Reconciliation header ──
  list: (params) => client.get(BR, { params }).then(unwrap),
  get:  (id)     => client.get(`${BR}/${encodeURIComponent(id)}`).then(unwrap),
  create: (payload) => client.post(BR, payload).then(unwrap),
  update: (id, payload) =>
    client.put(`${BR}/${encodeURIComponent(id)}`, payload).then(unwrap),
  remove: (id, { approvalMfaChallengeId } = {}) =>
    client.delete(`${BR}/${encodeURIComponent(id)}`,
                  mfaHeader(approvalMfaChallengeId)).then(unwrap),
  lock: (id, { approvalMfaChallengeId } = {}) =>
    client.post(`${BR}/${encodeURIComponent(id)}/lock`, {},
                mfaHeader(approvalMfaChallengeId)).then(unwrap),
  cancel: (id, { approvalMfaChallengeId } = {}) =>
    client.post(`${BR}/${encodeURIComponent(id)}/cancel`, {},
                mfaHeader(approvalMfaChallengeId)).then(unwrap),

  // ── Statement lines ──
  listStatementLines: (id) =>
    client.get(`${BR}/${encodeURIComponent(id)}/statement-lines`).then(unwrap),
  addStatementLine: (id, payload) =>
    client.post(`${BR}/${encodeURIComponent(id)}/statement-lines`, payload)
          .then(unwrap),
  updateStatementLine: (id, lineId, payload) =>
    client.put(`${BR}/${encodeURIComponent(id)}/statement-lines/${encodeURIComponent(lineId)}`,
               payload).then(unwrap),
  deleteStatementLine: (id, lineId) =>
    client.delete(`${BR}/${encodeURIComponent(id)}/statement-lines/${encodeURIComponent(lineId)}`)
          .then(unwrap),
  setStatementLineIgnore: (id, lineId, ignored) =>
    client.post(`${BR}/${encodeURIComponent(id)}/statement-lines/${encodeURIComponent(lineId)}/ignore`,
                { ignored: !!ignored }).then(unwrap),

  // ── Book candidates + matching ──
  listBookEntries: (id, params) =>
    client.get(`${BR}/${encodeURIComponent(id)}/book-entries`, { params })
          .then(unwrap),
  suggestions: (id) =>
    client.get(`${BR}/${encodeURIComponent(id)}/suggestions`).then(unwrap),
  match: (id, payload) =>
    client.post(`${BR}/${encodeURIComponent(id)}/matches`, payload).then(unwrap),
  unmatch: (id, matchId) =>
    client.delete(`${BR}/${encodeURIComponent(id)}/matches/${encodeURIComponent(matchId)}`)
          .then(unwrap),

  // ── BRS report ──
  report: (id) =>
    client.get(`${BR}/${encodeURIComponent(id)}/report`).then(unwrap),

  // ── Cheque tracking ──
  listCheques: (params) => client.get(CHQ, { params }).then(unwrap),
  listChequeEvents: (params) =>
    client.get(`${CHQ}/events`, { params }).then(unwrap),
  updateChequeStatus: (paymentId, payload, { approvalMfaChallengeId } = {}) =>
    client.post(`${CHQ}/${encodeURIComponent(paymentId)}/status`, payload,
                mfaHeader(approvalMfaChallengeId)).then(unwrap),

  // Bank ledger lookup — reuses the existing endpoint shipped by 6B.6.
  cashBankLookup: (q, limit = 100) =>
    client.get(`${BASE}/ledgers/cash-bank`, { params: { q, limit } })
          .then(unwrap)
};
